import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ErrorBoundary from './components/common/ErrorBoundary';
import GoogleAnalytics from './components/common/GoogleAnalytics';
import { AuthProvider } from './contexts/AuthContext';
import RequireAuth from './components/auth/RequireAuth';
import RouteHead from './seo/RouteHead';

// Import note pages
import NotesListPage from './pages/display/NotesListPage';
import NotePage from './pages/display/NotePage';
import CreateEditNotePage from './pages/contributor/CreateEditNotePage';
import NoteImpactPage from './pages/contributor/NoteImpactPage';

// Import display pages
import DisplaysListPage from './pages/contributor/DisplaysListPage';
import DisplayDetailPage from './pages/contributor/DisplayDetailPage';
import RegisterDisplayPage from './pages/contributor/RegisterDisplayPage';
import DisplayViewerPage from './pages/display/DisplayViewerPage';

// Import user pages
import ProfilePage from './pages/contributor/ProfilePage';
import EditProfilePage from './pages/contributor/EditProfilePage';
import DonationsPage from './pages/contributor/DonationsPage';

// Import additional pages as needed
import HomePage from './pages/public/HomePage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import AuthCallback from './components/auth/AuthCallback';
import DashboardPage from './pages/contributor/DashboardPage';
import PublicNoteSubmissionPage from './pages/public/PublicNoteSubmissionPage';
import NotAloneRedirect from './pages/public/NotAloneRedirect';
import NotFoundPage from './pages/public/NotFoundPage';

// Import moderator pages
import ModeratorQueuePage from './pages/moderator/ModeratorQueuePage';
import NoteModerationPage from './pages/moderator/NoteModerationPage';

// Import admin pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminActivityPage from './pages/admin/AdminActivityPage';
import PostsListPage from './pages/admin/PostsListPage';
import PostEditorPage from './pages/admin/PostEditorPage';

// Import public blog pages
import StoriesPage from './pages/public/StoriesPage';
import StoryDetailPage from './pages/public/StoryDetailPage';
import NewsPage from './pages/public/NewsPage';
import NewsDetailPage from './pages/public/NewsDetailPage';

// Create Unauthorized page component inline
const UnauthorizedPage = () => (
  <div className="container mt-5">
    <div className="row justify-content-center">
      <div className="col-md-6 text-center">
        <h1>Unauthorized</h1>
        <p>You don't have permission to access this page.</p>
        <a href="/dashboard" className="btn btn-primary">Go to Dashboard</a>
      </div>
    </div>
  </div>
);

function AppContent() {
  const location = useLocation();

  // Check if current route is a display viewer route
  const isDisplayViewerRoute = location.pathname.startsWith('/display/view/');

  // Render without header/footer for display viewer routes
  if (isDisplayViewerRoute) {
    return (
      <>
        <RouteHead />
        <GoogleAnalytics />
        <Routes>
          <Route path="/display/view" element={<DisplayViewerPage />} />
          <Route path="/display/view/:displayId" element={<DisplayViewerPage />} />
          <Route path="/display/view/:displayId/:token" element={<DisplayViewerPage />} />
        </Routes>
      </>
    );
  }

  // Regular app layout with header/footer for all other routes
  return (
    <div className="d-flex flex-column min-vh-100">
      <RouteHead />
      <GoogleAnalytics />
      <Header />
      <main id="main-content" className="flex-grow-1">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/submit" element={<PublicNoteSubmissionPage />} />
          <Route path="/notalone" element={<NotAloneRedirect />} />

          {/* Public Notes Routes */}
          <Route path="/notes" element={<NotesListPage />} />
          <Route path="/notes/:publicId" element={<NotePage />} />

          {/* Public Blog Routes */}
          <Route path="/stories" element={<StoriesPage />} />
          <Route path="/stories/:slug" element={<StoryDetailPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:slug" element={<NewsDetailPage />} />

          {/* Protected Contributor Routes */}
          <Route path="/my-notes" element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          } />
          <Route path="/my-notes/new" element={
            <RequireAuth>
              <CreateEditNotePage />
            </RequireAuth>
          } />
          <Route path="/my-notes/edit/:id" element={
            <RequireAuth>
              <CreateEditNotePage />
            </RequireAuth>
          } />
          <Route path="/my-notes/:id" element={
            <RequireAuth>
              <NotePage />
            </RequireAuth>
          } />
          <Route path="/my-notes/:id/impact" element={
            <RequireAuth>
              <NoteImpactPage />
            </RequireAuth>
          } />
          
          {/* Protected Display Routes */}
          <Route path="/my-displays" element={
            <RequireAuth>
              <DisplaysListPage />
            </RequireAuth>
          } />
          <Route path="/my-displays/register/:clientId" element={
            <RequireAuth>
              <RegisterDisplayPage />
            </RequireAuth>
          } />
          <Route path="/my-displays/:id" element={
            <RequireAuth>
              <DisplayDetailPage />
            </RequireAuth>
          } />
          
          {/* Protected User Routes */}
          <Route path="/profile" element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          } />
          <Route path="/donations" element={
            <RequireAuth>
              <DonationsPage />
            </RequireAuth>
          } />
          <Route path="/dashboard" element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          } />
          
          {/* Protected Dashboard Routes (with consistent URL structure) */}
          <Route path="/dashboard/profile" element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          } />
          <Route path="/dashboard/donations" element={
            <RequireAuth>
              <DonationsPage />
            </RequireAuth>
          } />
          
          {/* Protected Contributor Routes - new API path structure */}
          <Route path="/contributor/profile" element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          } />
          <Route path="/contributor/profile/edit" element={
            <RequireAuth>
              <EditProfilePage />
            </RequireAuth>
          } />
          <Route path="/contributor/donations" element={
            <RequireAuth>
              <DonationsPage />
            </RequireAuth>
          } />
          <Route path="/contributor/my-notes" element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          } />
          <Route path="/contributor/my-displays" element={
            <RequireAuth>
              <DisplaysListPage />
            </RequireAuth>
          } />
          <Route path="/contributor/dashboard" element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          } />
          
          {/* Protected Moderator Routes */}
          <Route path="/moderator/queue" element={
            <RequireAuth roles={['moderator', 'admin']}>
              <ModeratorQueuePage />
            </RequireAuth>
          } />
          <Route path="/moderator/notes/:id" element={
            <RequireAuth roles={['moderator', 'admin']}>
              <NoteModerationPage />
            </RequireAuth>
          } />
          
          {/* Protected Admin Routes */}
          <Route path="/admin/dashboard" element={
            <RequireAuth roles={['admin']}>
              <AdminDashboardPage />
            </RequireAuth>
          } />
          <Route path="/admin/users" element={
            <RequireAuth roles={['admin']}>
              <AdminUsersPage />
            </RequireAuth>
          } />
          <Route path="/admin/activity" element={
            <RequireAuth roles={['admin']}>
              <AdminActivityPage />
            </RequireAuth>
          } />
          <Route path="/admin/posts" element={
            <RequireAuth roles={['admin']}>
              <PostsListPage />
            </RequireAuth>
          } />
          <Route path="/admin/posts/new" element={
            <RequireAuth roles={['admin']}>
              <PostEditorPage />
            </RequireAuth>
          } />
          <Route path="/admin/posts/:id/edit" element={
            <RequireAuth roles={['admin']}>
              <PostEditorPage />
            </RequireAuth>
          } />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App; 